import Component from '@glimmer/component';

import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import fieldHeightOverlays from 'rise-ui/helpers/field-height-overlays.js';
import { animate } from 'rise-ui/utils/animate.js';
import { isOverflowing } from 'rise-ui/utils/dom.js';
import { registerClassModifier, destroyClassModifier, updateClassModifier } from 'rise-ui/utils/modifier.js';
import { MutationObserver } from 'rise-ui/utils/mutation-observer.js';
import { softSet, softSetProperties } from 'rise-ui/utils/object.js';
import { ResizeObserver } from 'rise-ui/utils/resize-observer.js';

export default class CFieldHeightComponent extends Component {
  fieldHeightClass = 'c-field-height';
  fieldHeightPrefixClass = 'c-field-height--';
  overflowClass = 'overflowing';

  @service fieldHeightOverlays;

  @tracked isReady = false;
  isOpen = false;
  isOverflowing = false;

  @action overlayEvent(e) {
    // Allows us to focus the target element programmatically
    if (this.ignoreEvent) {
      return;
    }
    const isOpen = this.isOverflowing && [ this.targetEl, this.overlayEl ]
      .some(n => n.contains(e.target));
    if (isOpen === this.isOpen) {
      return;
    }
    if (isOpen) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  @action onKeyUp(e) {
    if (this.isOpen && e.key === 'Escape') {
      this.hideOverlayByKey();
    }
  }

  @action showOverlay() {
    if (this.isOpen) {
      return;
    }
    this.isOpen = true;
    this.overlayEl.style.removeProperty('display');
    this.animation = animate(this.repositionOverlay, 60);
    this.repositionOverlay();
  }

  @action hideOverlay() {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.overlayEl.style.display = 'none';

    if (this.keyEvent) {
      this.ignoreEvent = true;
      this.targetEl.focus();
      this.ignoreEvent = false;
    }
    this.animation?.();
  }

  @action hideOverlayByKey() {
    if (!this.isOpen) {
      return;
    }
    this.keyEvent = true;
    this.hideOverlay();
    this.keyEvent = false;
  }

  @action resizeOverlay() {
    if (this.isOpen) {
      this.overlayEl.style.width = `${this.targetWidth}px`;
    }
  }

  @action repositionOverlay() {
    if (!this.isOpen) {
      return;
    }
    const { left, top, width } = this.targetEl.getBoundingClientRect();
    const { left: l, top: t } = this.fieldHeightOverlays.containerEl.getBoundingClientRect();
    softSetProperties(this.overlayEl.style, {
      left: `${left - l}px`,
      top: `${top - t}px`,
      width: `${width}px`,
    });
  }

  @action didInsertTabEdge(el) {
    el.setAttribute('tabindex', '0');
    el.addEventListener('focus', this.hideOverlayByKey);
    registerDestructor(this, () =>
      el.removeEventListener('focus', this.hideOverlayByKey)
    );
  }

  @action didInsertTarget(el) {
    this.targetEl = el;
    this.isReady = true;
    registerClassModifier(el, this.updateClasses);
    registerDestructor(this, () => {
      destroyClassModifier(el);
    });
  }

  @action didInsertOverlay(el) {
    this.overlayEl = el;
    Object.assign(el.style, {
      display: 'none',
      position: 'absolute',
    });
    document.addEventListener('mouseup', this.overlayEvent);
    document.addEventListener('touchend', this.overlayEvent);
    document.addEventListener('focusin', this.overlayEvent);
    document.addEventListener('keyup', this.onKeyUp);

    const resizeObserver = new ResizeObserver(([ entry ]) => {
      const cbs = entry.contentBoxSize;
      const cr = entry.contentRect;
      this.targetWidth = cbs ? cbs[0].inlineSize : cr.width;
      this.updateOverflowing();
      this.resizeOverlay();
    });
    resizeObserver.observe(this.targetEl);

    const mutationObserver = new MutationObserver(this.updateOverflowing);
    mutationObserver.observe(this.targetEl, { childList: true, subtree: true });

    this.updateClasses();

    registerDestructor(this, () => {
      document.removeEventListener('mouseup', this.overlayEvent);
      document.removeEventListener('touchend', this.overlayEvent);
      document.removeEventListener('focusin', this.overlayEvent);
      document.removeEventListener('keyup', this.onKeyUp);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      this.animation?.();
      const { height } = this.args;
      this.targetEl.classList.remove(...[
        this.fieldHeightClass,
        height && this.fieldHeightPrefixClass + height,
        this.overflowClass,
      ].filter(c => c));
    });
  }

  @action updateClasses() {
    const { classList: cl } = this.targetEl;
    cl.add(this.fieldHeightClass);
    const { height } = this.args;
    if (height) {
      cl.add(this.fieldHeightPrefixClass + height);
    }
    updateClassModifier(this.targetEl);
    this.updateOverflowing();
  }

  @action updateOverflowing() {
    const { targetEl: el, overflowClass } = this;
    const { classList: cl } = el;
    softSet(this, 'isOverflowing', isOverflowing(el));
    if (this.isOverflowing) {
      cl.add(overflowClass);
    } else {
      cl.remove(overflowClass);
    }
    updateClassModifier(el);
  }

  <template>
    <div tabindex="0"
      {{didInsert this.didInsertTarget}}
      ...attributes
    >
      {{yield}}
    </div>
    {{#if this.isReady}}
      {{#in-element (fieldHeightOverlays) insertBefore=null}}
        <div class="c-field-height--overlay"
          {{didInsert this.didInsertOverlay}}
          ...attributes
        >
          <div {{didInsert this.didInsertTabEdge}}></div>
          {{yield}}
          <div {{didInsert this.didInsertTabEdge}}></div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}
